import { Typography } from '@bowtie-ins/bowkit';
import { RadioOff, RadioOn, Timer } from '@bowtie-ins/bowkit/icons';
import {
  GRAPHITE,
  GREEN_DAY,
  HOTEL_CALIFORNIA,
  INK,
  ROCK,
  WHITE,
} from '@bowtie-ins/bowkit/palette';
import { DefaultCouponMapping } from '@bowtie-web/common';
import { Markdown } from '@bowtie-web/components';
import styled from '@emotion/styled';
import { alpha, Box, Divider } from '@material-ui/core';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CUSTOM_COMPONENTS } from '../CouponForm';

const EXTENSION_DAYS = 14;

type MenuItemProps = {
  variant: 'default' | 'custom-code' | 'enter-code' | 'saved';
  couponCode: string;
  message: string;
  displayEffectiveFrom?: string;
  displayEffectiveTo?: string;
  defaultCouponMapping?: DefaultCouponMapping;
  isSelected?: boolean;
  onClick: (couponCode: string) => void;
  dataUiComponent?: string;
  dataUiVariant?: string;
  dataUiAction?: string;
};

const ItemBox = styled(Box, {
  shouldForwardProp: props => !['isSelected', 'isExpired'].includes(props),
})<{ isSelected?: boolean; isExpired?: boolean }>(
  ({ isSelected, isExpired }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    backgroundColor: isSelected ? alpha(GREEN_DAY, 0.1) : 'transparent',
    color: isExpired ? GRAPHITE : INK,
    cursor: 'pointer',
    border: `1px solid ${isSelected ? GREEN_DAY : ROCK}`,
    position: 'relative',
    zIndex: isSelected ? 1 : 0,
    gap: 16,
    '&:first-of-type': {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    },
    '&:not(:first-of-type)': {
      marginTop: '-1px',
    },
    '&:last-of-type': {
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
  }),
);

const ContentBox = styled(Box, {
  shouldForwardProp: props => !['isExpired'].includes(props),
})<{ isExpired?: boolean }>(({ isExpired }) => ({
  opacity: isExpired ? 0.6 : 1,
}));

const MenuItem = ({
  variant,
  couponCode,
  message,
  displayEffectiveFrom,
  displayEffectiveTo,
  defaultCouponMapping,
  isSelected,
  onClick,
  dataUiComponent,
  dataUiVariant,
  dataUiAction,
}: MenuItemProps) => {
  const intl = useIntl();

  const isExpired = useMemo(() => {
    if (!displayEffectiveTo) return false;

    const effectiveTo = dayjs(displayEffectiveTo).tz().endOf('day');
    const now = dayjs();

    // Expired if end date has passed
    if (effectiveTo.isBefore(now)) return true;

    // Expired if end date is before or equal to start date (invalid date range)
    if (displayEffectiveFrom) {
      const effectiveFrom = dayjs(displayEffectiveFrom);
      if (effectiveTo.isSameOrBefore(effectiveFrom)) return true;
    }

    return false;
  }, [displayEffectiveFrom, displayEffectiveTo]);

  return (
    <ItemBox
      isSelected={isSelected}
      isExpired={isExpired}
      onClick={() => !isExpired && onClick(couponCode)}
      data-ui-component={dataUiComponent}
      data-ui-variant={dataUiVariant}
      data-ui-action={dataUiAction}
    >
      <Box display="grid" gridTemplateColumns="24px auto" gridGap={4}>
        <ContentBox isExpired={isExpired}>
          {isSelected ? <RadioOn style={{ color: GREEN_DAY }} /> : <RadioOff />}
        </ContentBox>
        <ContentBox isExpired={isExpired}>
          <Box display="flex" flexDirection="column" gridGap={4}>
            <Typography variant="title-xs">
              {['default', 'saved'].includes(variant) &&
              defaultCouponMapping?.type.name
                ? `${defaultCouponMapping.type.name}: `
                : ''}
              <Markdown
                options={{
                  overrides: { ...CUSTOM_COMPONENTS, remarks: () => <></> },
                }}
              >
                {message}
              </Markdown>
            </Typography>

            {couponCode && variant !== 'enter-code' && (
              <Box display="flex" flexDirection="column" gridGap={4}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  gridGap={4}
                >
                  <Box
                    py={0.25}
                    px={0.5}
                    borderRadius={4}
                    border={`1px solid ${ROCK}`}
                    bgcolor={WHITE}
                  >
                    <Typography
                      variant="body-s"
                      style={{
                        color: isExpired
                          ? HOTEL_CALIFORNIA
                          : isSelected
                            ? GREEN_DAY
                            : GRAPHITE,
                      }}
                    >
                      {couponCode}
                    </Typography>
                  </Box>
                </Box>

                {variant === 'saved' && displayEffectiveTo && !isExpired && (
                  <Typography variant="body-s" style={{ color: GREEN_DAY }}>
                    {intl.formatMessage(
                      { id: 'coupon.saved_extension' },
                      { days: EXTENSION_DAYS },
                    )}
                  </Typography>
                )}

                {isExpired ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    gridGap={4}
                    color={HOTEL_CALIFORNIA}
                  >
                    <Timer style={{ height: 16, width: 16 }} />
                    <Typography
                      variant="body-s"
                      style={{ color: HOTEL_CALIFORNIA }}
                    >
                      {intl.formatMessage({ id: 'coupon.expired' })}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    gridGap={4}
                    color={GREEN_DAY}
                  >
                    <Timer style={{ height: 16, width: 16 }} />
                    <Typography variant="body-s">
                      {dayjs(displayEffectiveTo)
                        .locale(intl.locale)
                        .format('LL')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </ContentBox>
      </Box>

      {defaultCouponMapping?.type.remark &&
        ['default', 'saved'].includes(variant) &&
        !isExpired && (
          <>
            <Divider
              style={{
                height: 0,
                borderTop: `1px dashed ${ROCK}`,
              }}
            />
            <Markdown
              options={{
                overrides: {
                  ...CUSTOM_COMPONENTS,
                  ul: ({ children }) => (
                    <ul style={{ margin: 0, paddingLeft: 24 }}>
                      <Typography variant="body-m" style={{ color: GRAPHITE }}>
                        {children}
                      </Typography>
                    </ul>
                  ),
                },
              }}
            >
              {defaultCouponMapping.type.remark}
            </Markdown>
          </>
        )}
    </ItemBox>
  );
};

export default MenuItem;
